import { useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Accessibility, Contrast, Type, Pause, Link2, Volume2, CircleStop, RotateCcw, X } from "lucide-react";
import { Dialogo } from "./Dialogo";
import {
  TAMANOS,
  cambiarPreferencias,
  detenerLectura,
  estaLeyendo,
  leerEnVozAlta,
  preferenciasActuales,
  puedeLeerEnVozAlta,
  restablecerPreferencias,
  suscribirLectura,
  suscribirPreferencias,
  textoParaLeer,
  type Preferencias,
  type Tamano,
} from "../lib/accesibilidad";

/**
 * El botón "Accesibilidad" y su panel.
 *
 * DÓNDE VIVE. Se monta UNA vez en main.tsx, al lado de <App />, y se pinta en
 * un portal sobre `<body>` como botón flotante. Se descartó ponerlo en la barra
 * superior de Layout porque ahí no llega a tres sitios donde hace falta:
 *   - la pantalla de entrada (no usa Layout), que es donde un adulto con la
 *     vista cansada necesita la letra grande para poder escribir su usuario;
 *   - una lección abierta, que oculta la barra lateral y la superior;
 *   - el móvil, donde la barra superior se sustituye por otra.
 * Flotante y fuera de las rutas, está en todas y nadie tiene que acordarse de
 * añadirlo a una pantalla nueva. Y fuera de `#root`, así "Leer en voz alta" no
 * se lee a sí mismo.
 *
 * El texto va pensado para que lo entienda un niño de primero y no le suene
 * infantil a un adulto: verbos cortos, qué hace cada cosa en una línea.
 */

const NOMBRES_TAMANO: Record<Tamano, string> = { 100: "Normal", 125: "Grande", 150: "Muy grande" };
// px y no rem a propósito (ver `.acc-tamano .acc-muestra` en accesibilidad.css).
const MUESTRA_PX: Record<Tamano, number> = { 100: 16, 125: 21, 150: 26 };

type Interruptor = {
  clave: Exclude<keyof Preferencias, "tamano">;
  titulo: string;
  ayuda: string;
  icono: ReactNode;
};

const INTERRUPTORES: Interruptor[] = [
  { clave: "contraste", titulo: "Alto contraste", ayuda: "Colores más fuertes para ver mejor.", icono: <Contrast size={22} aria-hidden /> },
  { clave: "dislexia", titulo: "Letra fácil de leer", ayuda: "Letras más claras y separadas.", icono: <Type size={22} aria-hidden /> },
  { clave: "sinMovimiento", titulo: "Sin animaciones", ayuda: "Nada se mueve en la pantalla.", icono: <Pause size={22} aria-hidden /> },
  { clave: "subrayarEnlaces", titulo: "Subrayar enlaces", ayuda: "Una raya debajo de lo que se puede pulsar.", icono: <Link2 size={22} aria-hidden /> },
];

function usePreferencias(): Preferencias {
  return useSyncExternalStore(suscribirPreferencias, preferenciasActuales, preferenciasActuales);
}

function useLeyendo(): boolean {
  return useSyncExternalStore(suscribirLectura, estaLeyendo, estaLeyendo);
}

export function PanelAccesibilidad() {
  const [abierto, setAbierto] = useState(false);
  const leyendo = useLeyendo();
  /*
   * Lo seleccionado se guarda AL PULSAR el botón, no al pulsar "Leer": abrir
   * el panel mueve el foco y en algunos navegadores (Safari en iPad) eso borra
   * la selección. Sin esto, "selecciona un párrafo y pide que te lo lean" leía
   * la página entera.
   */
  const seleccion = useRef("");

  function abrir() {
    try {
      seleccion.current = window.getSelection?.()?.toString() ?? "";
    } catch {
      seleccion.current = "";
    }
    setAbierto(true);
  }

  return createPortal(
    <>
      {/* Mientras lee y con el panel cerrado, un "Parar" a la vista: que la
          única forma de callarlo no sea volver a abrir el panel. */}
      {leyendo && !abierto && (
        <button type="button" className="acc-fab acc-fab-parar" onClick={detenerLectura}>
          <CircleStop size={24} aria-hidden />
          <span className="acc-fab-texto">Parar lectura</span>
        </button>
      )}
      {/* Solo el ícono, como en el portal de Vivienda Pública (decisión del
          usuario, 2026-09-29): es el símbolo que la gente ya reconoce. El
          nombre sigue en aria-label para el lector de pantalla y en title
          para quien pasa el ratón. */}
      <button
        type="button"
        className="acc-fab acc-fab-icono"
        onClick={abrir}
        aria-haspopup="dialog"
        aria-expanded={abierto}
        aria-label="Accesibilidad"
        title="Accesibilidad"
      >
        <Accessibility size={30} aria-hidden />
      </button>
      {abierto && <Panel seleccion={seleccion.current} onCerrar={() => setAbierto(false)} />}
    </>,
    document.body
  );
}

function Panel({ seleccion, onCerrar }: { seleccion: string; onCerrar: () => void }) {
  const p = usePreferencias();
  const leyendo = useLeyendo();
  const hayVoz = puedeLeerEnVozAlta();
  const [sinTexto, setSinTexto] = useState(false);

  function leer() {
    const ok = leerEnVozAlta(textoParaLeer(seleccion));
    setSinTexto(!ok);
  }

  return (
    <Dialogo tituloId="acc-titulo" onCerrar={onCerrar} cerrarAlClicFuera className="acc-panel" claseFondo="acc-fondo">
      <div className="acc-cabecera">
        <div>
          <h2 id="acc-titulo"><Accessibility size={26} aria-hidden /> Accesibilidad</h2>
          <p>Cambia cómo se ve y se escucha la pantalla.</p>
        </div>
        <button type="button" className="acc-cerrar" onClick={onCerrar} aria-label="Cerrar">
          <X size={22} aria-hidden />
        </button>
      </div>

      <div className="acc-grupo" role="group" aria-labelledby="acc-tamano-titulo">
        <p className="acc-grupo-titulo" id="acc-tamano-titulo">Tamaño del texto</p>
        <div className="acc-tamanos">
          {TAMANOS.map((t) => (
            <button
              key={t}
              type="button"
              className="acc-tamano"
              aria-pressed={p.tamano === t}
              onClick={() => cambiarPreferencias({ tamano: t })}
            >
              <span className="acc-muestra" aria-hidden style={{ fontSize: MUESTRA_PX[t] }}>A</span>
              <small>{NOMBRES_TAMANO[t]}</small>
            </button>
          ))}
        </div>
      </div>

      <div className="acc-grupo">
        {INTERRUPTORES.map((i) => (
          <button
            key={i.clave}
            type="button"
            className="acc-opcion"
            aria-pressed={p[i.clave]}
            onClick={() => cambiarPreferencias({ [i.clave]: !p[i.clave] })}
          >
            {i.icono}
            <span className="acc-opcion-texto">
              <span>{i.titulo}</span>
              <small>{i.ayuda}</small>
            </span>
            {/* aria-hidden: el estado ya lo anuncia `aria-pressed`; leerlo dos
                veces ("Alto contraste, Sí, botón activado") confunde. */}
            <span className="acc-estado" aria-hidden>{p[i.clave] ? "Sí" : "No"}</span>
          </button>
        ))}
      </div>

      <div className="acc-grupo" role="group" aria-labelledby="acc-voz-titulo">
        <p className="acc-grupo-titulo" id="acc-voz-titulo">Leer en voz alta</p>
        {!hayVoz ? (
          <p className="acc-nota">Este equipo no puede leer en voz alta.</p>
        ) : leyendo ? (
          <button type="button" className="acc-accion parar" onClick={detenerLectura}>
            <CircleStop size={22} aria-hidden /> Parar lectura
          </button>
        ) : (
          <button type="button" className="acc-accion primaria" onClick={leer}>
            <Volume2 size={22} aria-hidden /> {seleccion.trim() ? "Leer lo que marqué" : "Leer esta página"}
          </button>
        )}
        {hayVoz && (
          <p className="acc-nota" aria-live="polite">
            {sinTexto
              ? "No encontré texto para leer en esta pantalla."
              : "Consejo: marca un párrafo con el ratón o el dedo antes de abrir este panel y leeré solo ese."}
          </p>
        )}
      </div>

      <div className="acc-pie">
        <button type="button" className="acc-accion" onClick={restablecerPreferencias}>
          <RotateCcw size={20} aria-hidden /> Restablecer
        </button>
        <button type="button" className="acc-accion primaria" onClick={onCerrar}>
          Listo
        </button>
      </div>
    </Dialogo>
  );
}
